import type { AdminPickerDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PickerSourceBadge, PickerStateBadge } from './PickerBadges';
import { tokens } from '../../../design';
import { initials } from '../../../utils/initials';
import { shortId } from '../../inspectors/_lib/displayName';

/** Drawer header (AUT-400): avatar, name, «подборщик» + state + source, short id. */
export const PickerDetailHeader = ({
  picker,
  onClose,
}: {
  picker: AdminPickerDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const disabled = picker.disabled !== null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar
        sx={{ width: 52, height: 52, fontSize: 18, fontWeight: 700, opacity: disabled ? 0.5 : 1 }}
      >
        {initials(picker.name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}
          noWrap
          data-testid="picker-detail-name"
        >
          {picker.name ?? t('pickers.noValue')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" variant="outlined" color="info" label={t('pickers.detail.role')} />
          <PickerStateBadge disabled={disabled} testId="picker-detail-status" />
          <PickerSourceBadge source={picker.source} testId="picker-detail-source" />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(picker.userId)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('pickers.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
